import React, { useState, useEffect, useRef } from 'react';
import { getNewsList, searchNews, toggleNewsStatus, uploadNews } from '../../services/AppService';
import ConfirmActionModal from '../../components/AdminComponents/Modals/ConfirmActionModal';
import MessageModal from '../../components/AdminComponents/Modals/MessageModal';
import closeButton from '../../assets/images/close.png';
import { useNavigate } from 'react-router-dom';
import { resolveMediaUrl } from '../../utility/media';
import { BiTrash } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import UploadLoader from '../../components/UploadLoader';

const NewsPage = () => {
    const {t}=useTranslation();
    const navigate = useNavigate();

    // Form State
    const [headline, setHeadline] = useState("");
    const [newsType, setNewsType] = useState("");
    const [file, setFile] = useState(null);
    const [newsList, setNewsList] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const fileInputRef = useRef(null);

    // Modal States
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        payload: null
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: ""
    });

    const fetchNews = async () => {
        const res = await getNewsList(0, 50); // Fetching a larger list for display
        setNewsList(res?.content || []);
    };

    const handleNewsTypeChange = (e) => {
        const selectedType = e.target.value;
        setNewsType(selectedType);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const getAcceptType = () => {
        switch (newsType) {
            case "VIDEO":
                return "video/*,video/mp4,video/mkv,video/webm";
            case "VOICE":
                return "audio/*,audio/mp3,audio/wav,audio/m4a";
            case "PICTURE":
                return "image/*,image/jpeg,image/png,image/jpg";
            default:
                return undefined;
        }
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const handleUploadClick = () => {
        if (!headline || !newsType || !file) {
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: "Please fill headline, type and select a file"
            });
            return;
        }

        setConfirmModal({
            open: true,
            action: "upload",
            payload: null
        });
    };

    const handleConfirm = async () => {
        const { action, payload } = confirmModal;
        setConfirmModal({ open: false, action: null, payload: null });

        try {
            if (action === "upload") {
                setIsUploading(true);
                setUploadProgress(0);
                const newsData = { headline, newsType };
                await uploadNews(newsData, file, (progress) => {
                    setUploadProgress(progress);
                });

                setHeadline("");
                setNewsType("");
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "News uploaded successfully"
                });
            }

            if (action === "delete") {
                await toggleNewsStatus(payload);
                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "News deleted successfully"
                });
            }

            fetchNews();
        } catch (error) {
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: error?.response?.data?.message || "Something went wrong"
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    const handleDeleteClick = (id) => {
        setConfirmModal({
            open: true,
            action: "delete",
            payload: id
        });
    };

    return (
        <div className="bg-white p-3 md:p-6 rounded shadow-md">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-lg md:text-xl font-semibold text-blue uppercase">
                    {t('news.title')}
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            {/* Form Section */}
            <div className="bg-gray-50 p-4 rounded border mb-6">
                <div className="flex flex-col gap-4">
                    {/* Full Line Headline */}
                    <div className="w-full">
                        <label className="text-sm font-medium">{t('news.headline')}(<span className="text-danger">*</span>)</label>
                        <input
                            type="text"
                            placeholder="Enter News Headline"
                            value={headline}
                            onChange={(e) => setHeadline(e.target.value)}
                            className="border p-2 rounded w-full mt-1 focus:ring-2 focus:ring-blue-200 outline-none"
                        />
                    </div>

                    {/* Second Row: Type & Attachment */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium">{t('news.type')}(<span className="text-danger">*</span>)</label>
                            <select
                                value={newsType}
                                onChange={handleNewsTypeChange}
                                className="border p-2 rounded w-full mt-1"
                            >
                                <option value="">Select Type</option>
                                <option value="PICTURE">Picture</option>
                                <option value="VOICE">Voice</option>
                                <option value="VIDEO">Video</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">{t('common.addFile')}(<span className="text-danger">*</span>)</label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept={getAcceptType()}
                                onChange={(e) => setFile(e.target.files[0])}
                                className="border p-1 rounded w-full mt-1 bg-white"
                            />
                            {newsType && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {newsType === "VIDEO" ? "Supported: MP4, MKV, WebM, etc." : newsType === "VOICE" ? "Supported: MP3, WAV, M4A, etc." : "Supported: JPG, PNG, etc."}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center gap-3 mt-2">
                        <button onClick={handleUploadClick} className="staff-btn-save px-8 py-2">{t('news.add')}</button>
                        <button onClick={() => navigate('/smartcity/news/details')} className="btnView px-8 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition-colors">{t('news.view')}</button>                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="overflow-x-auto">
                <table className="w-full border text-center text-sm min-w-[800px]">
                    <thead className="bg-[#5f6db3] text-white">
                        <tr>
                            <th className="p-3 border w-12">{t('tables.srNo')}</th>
                            <th className="p-3 border text-left">{t('news.headline')}</th>
                            <th className="p-3 border w-24">{t('news.type')}</th>
                            <th className="p-3 border w-[400px]">{t('common.addedFiles')}</th>
                            <th className="p-3 border w-20">{t('common.delete')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {newsList.length === 0 ? (
                            <tr><td colSpan="5" className="p-10 text-gray-500">No news entries found</td></tr>
                        ) : (
                            newsList.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-gray-50 align-middle">
                                    <td className="border p-3">{idx + 1}</td>
                                    <td className="border p-3 text-left font-medium text-blue-800">{item.newsHeadline}</td>
                                    <td className="border p-3">{item.attachments?.[0]?.newsType}</td>
                                    <td className="border p-3">
                                        <div className="flex justify-center p-1">
                                            {item.attachments?.[0]?.newsType === "PICTURE" ? (
                                                <img
                                                    src={resolveMediaUrl(item.attachments[0].fileLocation)}
                                                    alt="preview"
                                                    className="w-full max-w-[400px] object-contain rounded border bg-white"
                                                />
                                            ) : item.attachments?.[0]?.newsType === "VIDEO" ? (
                                                <video className="w-full max-w-[400px] rounded border bg-black shadow-inner" controls>
                                                    <source src={resolveMediaUrl(item.attachments[0].fileLocation)} type="video/mp4" />
                                                </video>
                                            ) : (
                                                <div className="h-20 w-full max-w-[400px] flex flex-col items-center justify-center bg-gray-100 rounded border">
                                                    <audio controls className="w-full px-2">
                                                        <source src={resolveMediaUrl(item.attachments[0].fileLocation)} />
                                                    </audio>
                                                    <span className="text-[10px] mt-1 text-gray-500">Voice Content</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="border p-3">
                                        <button onClick={() => handleDeleteClick(item.id)} className="text-red-600 text-2xl">
                                            <BiTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <ConfirmActionModal
                open={confirmModal.open}
                onClose={() => setConfirmModal({ open: false })}
                onConfirm={handleConfirm}
                title={confirmModal.action === "upload" ? "Confirm Upload" : "Confirm Delete"}
                message={`Are you sure you want to ${confirmModal.action === "upload" ? "save this news entry" : "delete this news entry"}?`}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            {isUploading && (
                <UploadLoader
                    text={
                        newsType === "VIDEO"
                            ? "Uploading video, please wait..."
                            : newsType === "VOICE"
                            ? "Uploading audio, please wait..."
                            : "Uploading file, please wait..."
                    }
                    progress={uploadProgress}
                />
            )}
        </div>
    );
};

export default NewsPage;